import React, { useState, useEffect } from 'react';
import { socket } from '../Containers/MainContainer';
import Skeleton from 'react-loading-skeleton';
import { set } from 'nprogress';

// Mock socket for demonstration


// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Skeleton Loading Component


// Button Component
const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false }) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20",
    secondary: "bg-gray-700 hover:bg-gray-600 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${variants[variant]} ${sizes[size]} ${className} rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105`}
    >
      {children}
    </button>
  );
};

// Input Component
const Input = ({ type = "text", placeholder, value, onChange, className = "", id }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    className={`px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 backdrop-blur-sm ${className}`}
  />
);

// Card Component
const Card = ({ title, children, className = "", headerActions }) => (
  <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
    {title && (
      <div className="p-4 border-b border-gray-700/50 bg-gray-900/30">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {headerActions}
        </div>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

// Stat Card Component
const StatCard = ({ title, value, icon, color = "blue", isLoading = false }) => {
  const colorMap = {
    blue: 'from-blue-900/40 to-blue-800/40 border-blue-700/50',
    red: 'from-red-900/40 to-red-800/40 border-red-700/50',
    yellow: 'from-yellow-900/40 to-yellow-800/40 border-yellow-700/50'
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} p-4 rounded-xl border backdrop-blur-sm hover:bg-opacity-60 transition-all duration-300`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {icon && <span className="text-xl">{icon}</span>}
            <p className="text-gray-300 text-xs font-medium uppercase tracking-wide">{title}</p>
          </div>
          {isLoading ? (
            <Skeleton className="w-20 h-7" />
          ) : (
            <p className="text-2xl font-bold text-white">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Table Component
const Table = ({ headers, children, isLoading = false }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-900/50">
        <tr>
          {headers.map((header, index) => (
            <th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700/50">
        {isLoading ? (
          Array.from({ length: 10 }, (_, index) => (
            <tr key={index} className="hover:bg-gray-800/30">
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} className="px-4 py-4">
                  <Skeleton className="w-full h-4" />
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div className="flex justify-center items-center space-x-2 mt-4">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(1)}
        disabled={currentPage === 1}
      >
        First
      </Button>
      
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </Button>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
          className="min-w-[40px]"
        >
          {page}
        </Button>
      ))}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage + 1)}
        disabled={currentPage === pageNumber}
      >
        Next
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(pageNumber)}
        disabled={currentPage === pageNumber}
      >
        Last
      </Button>
    </div>
  );
};

// Main Component
const PlutoOfferDataTotal = () => {
  const [userProfile, setUserProfile] = useState({});
  const [earnings, setEarnings] = useState([]);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [jumpPage, setJumpPage] = useState('');

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getCurrentMonthNumber = () => {
    return new Date().getMonth();
  };

  const getMonthName = (offset) => {
    const monthIndex = getCurrentMonthNumber() - offset;
    return monthNames[monthIndex < 0 ? 12 + monthIndex : monthIndex];
  };

  useEffect(() => {
    socket.on('response_get_admin_offersdata', (data) => {
      setEarnings(data.withdrawals);
      setPageNumber(Math.ceil(data.withdrawalsLength / 10));
      setUserProfile(data);
      setLoading(false);
    });

    socket.emit('getOffersDataAdmin', {
      offers_like: username,
      page,
    });

    return () => {
      socket.off('response_get_admin_offersdata');
    };
  }, []);

  const handleSearch = (value) => {
    setUsername(value);
    setLoading(true);
    setPage(1);
   
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setLoading(true);
    socket.emit('getOffersDataAdmin', {
      offers_like: username,
      page: newPage,
    });
  };

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpPage);
    if (pageNum && pageNum > 0 && pageNum <= pageNumber) {
      handlePageChange(pageNum);
      setJumpPage('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
     

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Statistics */}
      

        {/* Search and Filters */}
        <Card title="Search Offers">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                placeholder="Search by offer name..."
                value={username}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full"
              />
            </div>
            <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => { 
                    setLoading(true);
                    setEarnings([]);
                    socket.emit('getOffersDataAdmin', {
      offers_like: username.toLowerCase(),
      page: 1,
    });}}
              >
                Search
              </Button>
            {username && (
              <Button
                variant="secondary"
                onClick={() => handleSearch('')}
              >
                Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Data Table */}
        <Card 
          title="Suspect Offers"
          headerActions={
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400">
                {username && `Search: "${username}" • `}
                Page {page} of {pageNumber} ({earnings.length} items)
              </span>
              
            </div>
          }
        >
          <Table
            headers={[
              'Offer Name',
              'Wall',
              'Completed',
              'Total CB',
              getMonthName(1),
              getMonthName(2),
              getMonthName(3),
              getMonthName(4),
              getMonthName(5),
              getMonthName(6)
            ]}
            isLoading={loading && earnings.length === 0}
          >
            {earnings.map((earning, i) => (
              <tr key={earning.id || i} className="hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-4 text-sm text-white max-w-xs">
                  <div className="truncate" title={earning.name}>
                    {earning.name}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <span className="px-2 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">
                    {earning.wall}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-green-400 font-medium">
                  {numberWithCommas(earning.count)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    earning.chargebacks > 20 ? 'bg-red-900/50 text-red-200' :
                    earning.chargebacks > 10 ? 'bg-orange-900/50 text-orange-200' :
                    'bg-gray-700/50 text-gray-300'
                  }`}>
                    {numberWithCommas(earning.chargebacks)}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {earning.cbMonth1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {earning.cbMonth2}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {earning.cbMonth3}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {earning.cbMonth4}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {earning.cbMonth5}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {earning.cbMonth6}
                </td>
              </tr>
            ))}
          </Table>

          {/* Pagination */}
          {pageNumber > 0 && (
            <div className="mt-6 flex justify-between items-center">
              <PaginationPluto
                pageNumber={pageNumber}
                currentPage={page}
                requestNewList={handlePageChange}
              />
              
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  placeholder="Page"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  className="w-20"
                  id="jumptototal"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleJumpToPage}
                >
                  Go
                </Button>
              </div>
            </div>
          )}

          {earnings.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-lg font-medium text-white mb-2">No offers found</h3>
              <p className="text-gray-400">
                {username 
                  ? `No offers found matching "${username}". Try adjusting your search term.`
                  : 'No suspect offers found. Try adjusting your search criteria.'
                }
              </p>
              {username && (
                <div className="mt-4">
                  <Button variant="secondary" onClick={() => handleSearch('')}>
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default PlutoOfferDataTotal;